import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import {
  DEFAULT_LOCALE,
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  normalizeLocale,
  updateSearchWithLocale,
  writeStoredLocale,
} from '@gift-i18n';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { beginLocaleTransition } from '@/i18n/localeTransition';

export function LanguageSelector() {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation(['common']);
  const value = normalizeLocale(i18n.resolvedLanguage || i18n.language) || DEFAULT_LOCALE;

  const handleValueChange = async (next: string) => {
    const normalized = normalizeLocale(next) || DEFAULT_LOCALE;
    const nextSearch = updateSearchWithLocale(location.search, normalized);
    beginLocaleTransition(normalized);

    if (import.meta.env.DEV) {
      console.debug('[i18n:web] selector-change', {
        currentLocale: value,
        nextLocale: normalized,
        fromSearch: location.search,
        toSearch: nextSearch,
      });
    }

    writeStoredLocale(window.localStorage, normalized);
    await i18n.changeLanguage(normalized);
    if (nextSearch !== location.search) {
      navigate({ pathname: location.pathname, search: nextSearch, hash: location.hash }, { replace: true });
    }
  };

  return (
    <Select value={value} onValueChange={handleValueChange}>
      <SelectTrigger className="h-9 w-[180px] gap-2 text-xs sm:text-sm" aria-label={t('common:language')}>
        <Globe className="h-4 w-4 text-muted-foreground" />
        <SelectValue>{LOCALE_LABELS[value]}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {SUPPORTED_LOCALES.map((locale) => (
          <SelectItem key={locale} value={locale}>
            {LOCALE_LABELS[locale]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
